/**
 * Pure idempotency-key formulae. Same input → same key (deterministic),
 * different input → different key. Re-running a half-failed commit is safe
 * because every successful prior call returns the same entity, and every
 * retried failed call uses the same key the BFF previously saw.
 */

const norm = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, '-');

export const merchantKey = (tin: string): string => `merchant:${tin}`;

export const merchantLocationKey = (tin: string, npi2: string): string =>
  `merchant-location:${tin}:${npi2}`;

export const providerIndividualKey = (npi1: string): string =>
  `provider-individual:${npi1}`;

export const accessPointKey = (
  merchantLocationId: string,
  providerIndividualId: string,
): string => `access-point:${merchantLocationId}:${providerIndividualId}`;

export const procedureCodeKey = (codeset: string, code: string): string =>
  `procedure-code:${codeset}:${code}`;

export const itemKey = (name: string): string => `item:${norm(name)}`;

export const linkPcToItemKey = (
  itemId: string,
  procedureCodeId: string,
): string => `link-pc-to-item:${itemId}:${procedureCodeId}`;

export const linkMerchantLocationKey = (
  merchantLocationId: string,
  merchantId: string,
): string => `link-merchant-location:${merchantLocationId}:${merchantId}`;

export const bundleKey = (name: string, anchorCount: number): string =>
  `bundle:${norm(name)}:${anchorCount}`;

export const organizationKey = (taxId: string): string =>
  `organization:${taxId}`;

export const providerAccountKey = (taxId: string): string =>
  `provider-account:${taxId}`;

export const feeScheduleKey = (name: string, effectiveStart: string): string =>
  `fee-schedule:${norm(name)}:${effectiveStart}`;

export const contractKey = (
  customerAccountId: string,
  effectiveStart: string,
): string => `contract:${customerAccountId}:${effectiveStart}`;

export const linkFeeScheduleToContractKey = (
  contractId: string,
  feeScheduleId: string,
): string => `link-fs:${contractId}:${feeScheduleId}`;

export const cspBulkKey = (
  contractId: string,
  effectiveStart: string,
): string => `csp-bulk:${contractId}:${effectiveStart}`;

export const feeScheduleStateKey = (
  feeScheduleId: string,
  state: string,
): string => `fs-state:${feeScheduleId}:${state}`;

export const contractStateKey = (
  contractId: string,
  state: string,
  version: number,
): string => `contract-state:${contractId}:${state}:${version}`;
